import { useMemo } from "react";
import { LegendKey } from "../../../../models/chart_model";
import {
  getColor,
  getDistinctColorsForKeys,
  getForecastBaseName,
  getForecastVariantColor,
  isClearlyDifferentColorPair,
  isColorDistinctFrom,
  isForecastSeriesName,
} from "../../../../utility/Colors";
import { compareLegends } from "../utils/legendUtils";
import { getLegendColorKey } from "../utils/colorUtils";

const normalizeName = (value?: string) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/**
 * Bundles 3 chained derivations:
 *  - filteredLegends: legends excluding xDataKey
 *  - colorMap: stable RESOLVED color per legend key (cached on key signature).
 *    Predefined legendColor/stroke values are absorbed as-is, then forecast
 *    series whose color is too similar to any other series on the chart are
 *    re-colored with a distinct variant derived from their base series. This
 *    is the single global fix-up point — pages do not need per-file changes.
 *  - orderedSelectedLegends: selectedLegends sorted via compareLegends
 */
export function useLegendDerivations(
  legends: LegendKey[],
  selectedLegends: LegendKey[],
  xDataKey: string,
  customLegendOrder?: string[],
) {
  const filteredLegends = useMemo(
    () => legends.filter((e) => e.dataKey !== xDataKey),
    [legends, xDataKey],
  );

  // Cache key — stable string of color keys AND predefined colors. Avoids
  // recomputing when legend identities are unchanged but array reference
  // flipped; recomputes when a page changes a predefined stroke.
  const colorKeySignature = useMemo(
    () =>
      filteredLegends
        .map(
          (l, i) =>
            `${getLegendColorKey(l, i)}:${l.legendColor ?? l.stroke ?? ""}`,
        )
        .join("|"),
    [filteredLegends],
  );

  const colorMap = useMemo(() => {
    const entries = filteredLegends.map((legend, index) => ({
      legend,
      key: getLegendColorKey(legend, index),
    }));

    const distinctColors = getDistinctColorsForKeys(
      entries.map((e) => e.key),
    ) as Record<string, string>;

    // Pass 1 — resolve every series: predefined colors win, others take the
    // hashed distinct color (previous behavior, unchanged).
    const map: Record<string, string> = {};
    entries.forEach(({ legend, key }, index) => {
      map[key] =
        legend.legendColor ??
        legend.stroke ??
        distinctColors[key] ??
        getColor(index, key);
    });

    // Pass 2 — forecast collision fix-up. Only forecast-named series are ever
    // adjusted; non-forecast predefined colors are never touched.
    entries.forEach(({ legend, key }) => {
      const displayName = legend.name ?? legend.dataKey;
      if (!isForecastSeriesName(displayName)) return;

      const otherColors = entries
        .filter((e) => e.key !== key)
        .map((e) => map[e.key])
        .filter(Boolean);
      const baseName = normalizeName(getForecastBaseName(displayName) ?? "");
      const baseEntry = baseName
        ? entries.find(
            (e) =>
              e.key !== key &&
              normalizeName(e.legend.name ?? e.legend.dataKey) === baseName,
          )
        : undefined;

      // Recolor when too close to anything on the chart, or when the pair
      // with its own base series fails the stricter same-name test.
      const collidesWithChart = !isColorDistinctFrom(map[key], otherColors);
      const collidesWithBase =
        !!baseEntry && !isClearlyDifferentColorPair(map[key], map[baseEntry.key]);
      if (!collidesWithChart && !collidesWithBase) return;

      const anchorColor = baseEntry ? map[baseEntry.key] : map[key];
      map[key] = getForecastVariantColor(anchorColor, otherColors);
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorKeySignature]);

  const orderedSelectedLegends = useMemo(() => {
    const base =
      selectedLegends.length === 0
        ? filteredLegends
        : legends.filter((legend) =>
            selectedLegends.some(
              (selected) => selected.dataKey === legend.dataKey,
            ),
          );
    return [...base].sort((a, b) =>
      compareLegends(a.name || "", b.name || "", customLegendOrder),
    );
  }, [selectedLegends, filteredLegends, legends, customLegendOrder]);

  return { filteredLegends, colorMap, orderedSelectedLegends };
}
